import axios from "axios";
import API_ENDPOINTS from "../config/api";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from "../components/Modal";
import DateRange from "../components/DateRange";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const TilesSection = styled.section`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 2rem;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.5rem;
  display: flex;
  gap: 1.5rem;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 380px;
  background: white;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 12px 12px 32px -15px rgba(66, 68, 90, 1);
    border-color: #013566;
  }

  a {
    text-decoration: none;
    color: #013566;
    font-size: 1.4rem;
    font-weight: 600;
    cursor: pointer;
    text-align: center;
    transition: color 0.2s ease;

    &:hover {
      color: #012a4a;
    }
  }

  button {
    width: 100%;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    border: none;
    outline: none;
    border-radius: 8px;
    color: white;
    background: linear-gradient(135deg, #013566 0%, #012a4a 100%);
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: linear-gradient(135deg, #012a4a 0%, #013566 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(1, 53, 102, 0.3);
    }
  }
`;

const SingleStatDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  p {
    font-weight: 500;
    font-size: 1rem;
    color: #333;
    margin: 0;
  }

  p:last-child {
    font-weight: 600;
    color: #013566;
    font-size: 1.1rem;
  }
`;

const VisitPage = styled.a`
  font-size: 0.9rem !important;
  text-decoration: none !important;
  font-weight: 600;
  width: 100%;
  text-align: center !important;
  cursor: pointer;
  color: #013566 !important;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: #f8f9fa;
  border: 1px solid #e9ecef;

  &:hover {
    background: #013566;
    color: white !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(1, 53, 102, 0.2);
  }
`;

const ActivityBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const LiveDataBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
`;

const SortSelect = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;

  p {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #013566;
  }

  select {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 180px;

    &:hover {
      border-color: #013566;
    }

    &:focus {
      outline: none;
      border-color: #013566;
      box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
    }
  }
`;

interface Props {
  startDate: Date | null;
  endDate: Date | null;
  handleStartDateChange: (date: Date | null) => void;
  handleEndDateChange: (date: Date | null) => void;
}

const Dashboard: React.FC<Props> = ({
  startDate,
  endDate,
  handleEndDateChange,
  handleStartDateChange,
}) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const [sortBy, setSortBy] = useState<string>("page_views_count");
  const [sortDirection, setSortDirection] = useState<string>("desc");
  const [sites, setSites] = useState<any[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>("");

  const modalRef = useRef(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.SITES, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setSites(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    };

    fetchSites();
  }, [token]);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);

      const formattedStartDate = startDate
        ? startDate.toISOString().split("T")[0]
        : "";
      const formattedEndDate = endDate
        ? endDate.toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      try {
        const response = await axios.get(API_ENDPOINTS.STATS, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            site: selectedSite || null,
            sort: sortBy ? sortBy : null,
            direction: sortDirection ? sortDirection : null,
          },
        });
        setStats(response.data);
        setLoading(false);
      } catch (error: any) {
        setError("Error fetching data");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token, startDate, endDate, sortBy, sortDirection, selectedSite]); // Add startDate and endDate as dependencies

  const handleSeeMore = (id: string, name: string) => {
    navigate(`/website/${id}`);
    localStorage.setItem("projectName", name);
  };

  const handleCloseModal = () => {
    setModalData(null);
    setShowModal(false);
  };

  return (
    <Container>
      <PageTitle>Dashboard</PageTitle>
      <DateRange
        startDate={startDate}
        endDate={endDate}
        handleEndDateChange={handleEndDateChange}
        handleStartDateChange={handleStartDateChange}
      />
      <SortSelect>
        <div>
          <p>Sort By</p>
          <select
            value={sortBy ?? ""}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="" disabled>
              Sort By
            </option>
            <option value="clicks_count">Clicks Count</option>
            <option value="form_submissions_count">
              Form Submissions Count
            </option>
            <option value="page_views_count">Page Views Count</option>
            <option value="conversions">Conversions</option>
            <option value="unique_views">Unique Views</option>
          </select>
        </div>
        <div>
          <p>Website</p>
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
          >
            <option value="">All Websites</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <p>Sort Order</p>
          <select
            value={sortDirection ?? ""}
            onChange={(e) => setSortDirection(e.target.value)}
          >
            <option value="" disabled>
              Sort Direction
            </option>
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>
        </div>
      </SortSelect>
      {loading ? (
        <ActivityBox>
          <CircularProgress />
        </ActivityBox>
      ) : (
        <TilesSection>
          {stats?.map((stat: any, index: number) => {
            return (
              <Tile key={index}>
                <a href={stat?.url} target="_blank" rel="noreferer">
                  {stat?.name}
                </a>
                <LiveDataBadge>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  Live Data
                </LiveDataBadge>

                <SingleStatDiv>
                  <p>Total Page Views:</p>
                  <p>{stat?.page_views_count}</p>
                </SingleStatDiv>
                <SingleStatDiv>
                  <p>Unique Page Views:</p>
                  <p>{stat?.unique_views}</p>
                </SingleStatDiv>
                <SingleStatDiv>
                  <p>Total Form Submissions:</p>
                  <p>{stat?.form_submissions_count}</p>
                </SingleStatDiv>
                <SingleStatDiv>
                  <p>Total Clicks</p>
                  <p>{stat?.clicks_count}</p>
                </SingleStatDiv>
                <SingleStatDiv>
                  <p>Conversion Rate</p>
                  <p>{stat?.conversions} %</p>
                </SingleStatDiv>

                <VisitPage href={stat?.url} target="_blank" rel="noreferer">
                  Visit Page
                </VisitPage>

                <button onClick={() => handleSeeMore(stat?.id, stat?.name)}>
                  See More Details
                </button>
              </Tile>
            );
          })}
        </TilesSection>
      )}

      {showModal && (
        <Modal
          forwardRef={modalRef}
          closeModal={handleCloseModal}
          modalData={modalData}
        />
      )}
    </Container>
  );
};

export default Dashboard;
